/**
 * Rapsodo Pitching 2.0 CSV → canonical pitch objects.
 *
 * One source of truth for: column names, type coercion, garbage-row filtering,
 * timestamp parsing (UTC), and spin-direction clock notation.
 *
 * Imports of this module: scripts/seed (regen), upload flow (Phase 1+),
 * unit tests. Do not parse Rapsodo CSVs anywhere else.
 *
 * Schema sourced from a real export (Tyler Townend, 4/28/2026 bullpen);
 * if Rapsodo ever changes their headers, this is the only file to update.
 */

import Papa from 'papaparse';

// ============================================================
// Column map: Rapsodo CSV header → canonical (snake_case) field name.
// 34 columns total in Pitching 2.0 exports.
// ============================================================
export const RAPSODO_PITCHING_2_COLUMNS = {
  'Game Pitch':                  'game_pitch_num',
  'Pitch Created At':            'thrown_at_raw',
  'PitchID':                     'rapsodo_pitch_id',
  'Pitch Type':                  'pitch_type',
  'Is Strike':                   'is_strike',
  'PlateLocSide (Inches)':       'plate_loc_side_in',
  'PlateLocHeight (Inches)':     'plate_loc_height_in',
  'RelSpeed (MPH)':              'rel_speed_mph',
  'SpinRate':                    'total_spin_rpm',
  'TrueSpinRelease':             'true_spin_rpm',
  'Spin Efficiency':             'spin_eff_pct',
  'Spin Direction':              'spin_dir_clock',
  'InducedVertBreak (Inches)':   'ind_vert_break_in',
  'HorzBreak (Inches)':          'horz_break_in',
  'HorzRelAngle':                'horz_rel_angle',
  'VertRelAngle':                'vert_rel_angle',
  'RelHeight (Feet)':            'rel_height_ft',
  'RelSide (Feet)':              'rel_side_ft',
  'Gyro Degree (deg)':           'gyro_deg',
  'Right Hand Spin Direction':   'rh_spin_dir',
  'Right Hand Total Spin':       'rh_total_spin',
  'Left Hand Spin Direction':    'lh_spin_dir',
  'Left Hand Total Spin':        'lh_total_spin',
  'IntentType':                  'intent_type',
  'Pitcher First Name':          'pitcher_first',
  'Pitcher Last Name':           'pitcher_last',
  'Device Serial Number':        'device_serial',
  'Latitude':                    'latitude',
  'Longitude':                   'longitude',
  'VerticalSSWBreak (Feet)':     'ssw_vert_ft',
  'HorizontalSSWBreak (Feet)':   'ssw_horz_ft',
  'Flagged':                     'flagged',
  'VAA':                         'vaa',
  'HAA':                         'haa',
};

const INTEGER_FIELDS = new Set([
  'game_pitch_num', 'total_spin_rpm', 'true_spin_rpm', 'gyro_deg',
  'rh_total_spin', 'lh_total_spin',
]);

const NUMERIC_FIELDS = new Set([
  'plate_loc_side_in', 'plate_loc_height_in',
  'rel_speed_mph', 'spin_eff_pct',
  'ind_vert_break_in', 'horz_break_in',
  'horz_rel_angle', 'vert_rel_angle',
  'rel_height_ft', 'rel_side_ft',
  'ssw_vert_ft', 'ssw_horz_ft',
  'vaa', 'haa',
  'latitude', 'longitude',
]);

const BOOLEAN_FIELDS = new Set(['flagged']);

// ============================================================
// clockToDegrees: Rapsodo's "1:22" spin direction → 0-360°
// 12:00 = 0°, 30° per hour, 0.5° per minute.
// ============================================================
export function clockToDegrees(input) {
  if (!input || typeof input !== 'string') return null;
  const m = input.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const hours = parseInt(m[1], 10);
  const minutes = parseInt(m[2], 10);
  if (hours < 0 || hours > 12 || minutes < 0 || minutes >= 60) return null;
  return (hours % 12) * 30 + minutes * 0.5;
}

// ============================================================
// parseRapsodoTimestamp: "MM-DD-YYYY HH:MM:SS" (no TZ) → UTC ISO string.
// Rapsodo's exports have no timezone marker; cloud-side stamps appear to
// be UTC based on the actual times in Tyler's CSV (afternoon CT bullpen
// stamped 23:xx UTC = 5-6 PM CT).
// ============================================================
export function parseRapsodoTimestamp(input) {
  if (!input || typeof input !== 'string') return null;
  const m = input.match(/^(\d{2})-(\d{2})-(\d{4})\s+(\d{2}):(\d{2}):(\d{2})$/);
  if (!m) return null;
  const [, mm, dd, yyyy, h, mi, s] = m;
  return `${yyyy}-${mm}-${dd}T${h}:${mi}:${s}.000Z`;
}

// ============================================================
// extractSessionId: Rapsodo filename → MongoDB ObjectId.
// Filename pattern: PracticeSession_<24hex>_Data_PITCHPARAMS_<unix>.csv
// Used as the natural unique key on rapsodo_uploads (dedup re-uploads).
//
// Returns null for coach-renamed files (e.g. "2026-05-10_bullpen_pt_2.csv").
// Those fall through to isRapsodoCsv() for type detection and to the
// source descriptor's content-based dedup-key fallback (first PitchID).
// ============================================================
export function extractSessionId(filenameOrPath) {
  if (!filenameOrPath || typeof filenameOrPath !== 'string') return null;
  const m = filenameOrPath.match(/PracticeSession_([0-9a-f]{24})_Data_/i);
  return m ? m[1] : null;
}

// ============================================================
// isRapsodoCsv: content sniffer — peek at the header row to recognize
// Rapsodo Pitching 2.0 exports regardless of filename. Coaches commonly
// rename Rapsodo exports for organization ("2026-05-10_bullpen_pt_2.csv"),
// which breaks filename-based routing. Header columns are stable across
// renames, so we use them as the authoritative signal.
//
// Signature: two columns that are very Rapsodo-specific and not present
// in Pocket Radar or any other CSV we expect — "Pitch Created At" and
// "PitchID". Both must be present.
// ============================================================
export function isRapsodoCsv(csvText) {
  if (!csvText || typeof csvText !== 'string') return false;
  const firstLine = csvText.split(/\r?\n/, 1)[0] ?? '';
  return firstLine.includes('Pitch Created At') && firstLine.includes('PitchID');
}

// ============================================================
// isValidPitch: filter Rapsodo "no-read" rows.
// When Rapsodo can't track a pitch (warmup marker, ball missed the radar,
// failed read) it emits an OTH row with all-zeros for velocity/spin.
// ============================================================
export function isValidPitch(pitch) {
  if (!pitch) return false;
  const speed = pitch.rel_speed_mph ?? 0;
  const spin = pitch.total_spin_rpm ?? 0;
  return speed > 0 || spin > 0;
}

// ============================================================
// normalizePitch: one Rapsodo CSV row (PapaParse object) → canonical pitch.
// Preserves the original row under .raw for debugging / forward-compat.
// ============================================================
export function normalizePitch(rawRow) {
  const pitch = { raw: rawRow };
  for (const [csvKey, canonicalKey] of Object.entries(RAPSODO_PITCHING_2_COLUMNS)) {
    pitch[canonicalKey] = coerceField(canonicalKey, rawRow[csvKey]);
  }
  pitch.thrown_at = parseRapsodoTimestamp(pitch.thrown_at_raw);
  delete pitch.thrown_at_raw;
  return pitch;
}

function coerceField(canonicalKey, rawValue) {
  if (rawValue === undefined || rawValue === null || rawValue === '' || rawValue === '--') {
    return null;
  }
  if (canonicalKey === 'is_strike') {
    return rawValue === 'Y' || rawValue === 'y' || rawValue === 'true' || rawValue === true;
  }
  if (BOOLEAN_FIELDS.has(canonicalKey)) {
    return rawValue === 'true' || rawValue === true;
  }
  if (INTEGER_FIELDS.has(canonicalKey)) {
    const n = parseInt(rawValue, 10);
    return Number.isFinite(n) ? n : null;
  }
  if (NUMERIC_FIELDS.has(canonicalKey)) {
    const n = parseFloat(rawValue);
    return Number.isFinite(n) ? n : null;
  }
  return String(rawValue);
}

// ============================================================
// parseRapsodoCsv: full pipeline, CSV text → normalized + filtered + sorted.
// Returns { pitches, filteredCount, unknownColumns }.
// pitches are sorted by thrown_at ascending (chronological order).
// unknownColumns surfaces Rapsodo schema drift loudly.
// ============================================================
export function parseRapsodoCsv(csvText) {
  const result = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: true,
  });

  const pitches = [];
  let filteredCount = 0;

  for (const row of result.data) {
    const normalized = normalizePitch(row);
    if (isValidPitch(normalized)) {
      pitches.push(normalized);
    } else {
      filteredCount++;
    }
  }

  pitches.sort((a, b) => a.thrown_at.localeCompare(b.thrown_at));

  const knownColumns = new Set(Object.keys(RAPSODO_PITCHING_2_COLUMNS));
  const csvHeaders = result.meta?.fields ?? [];
  const unknownColumns = csvHeaders.filter((h) => !knownColumns.has(h));

  return { pitches, filteredCount, unknownColumns };
}
